<template>
  <div class="point-sources field-row mb--30">
    <div class="field-item field-item--grow field-item--column">
      <label class="field-label mb--15">{{ t('popup.content.sources.label') }}</label>
      <div class="point-sources__row">
        <button
          ref="trigger"
          type="button"
          class="button button--popup point-sources__trigger"
          :class="{ disabled: !canAttach }"
          :title="canAttach ? t('popup.content.sources.title') : t('popup.content.sources.unavailable')"
          :aria-label="t('popup.content.sources.title')"
          :aria-disabled="canAttach ? undefined : 'true'"
          aria-haspopup="dialog"
          @click="openPopup"
        >
          <i class="fas" :class="uploading ? 'fa-spinner fa-spin' : 'fa-paperclip'" aria-hidden="true"></i>
          <span v-if="documents.length" class="point-sources__count">{{ documents.length }}</span>
        </button>
        <p class="point-sources__hint">{{ t('popup.content.sources.hint') }}</p>
      </div>
    </div>
  </div>
  <popup-source-documents
    :open="open"
    :documents="documents"
    :uploading="uploading"
    :error="error"
    @close="closePopup"
    @upload="upload"
    @attach-url="attachUrl"
    @remove="remove"
  ></popup-source-documents>
</template>

<script setup lang="ts">
// The source documents of the point a content form edits: a paperclip counting
// them, beside what they are for, opening a popup to add or delete one — as
// the Quick Edit Bar does with its popover. Every ready document grounds the
// text generated for the point. Until an LLM is connected the paperclip is
// disabled and says why; the `disabled` class and `aria-disabled`, not
// `disabled`, keep its tooltip showing.

import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import PopupSourceDocuments from './PopupSourceDocuments.vue';
import { usePointSourceDocuments } from '../composables/usePointSourceDocuments';
import type { StatusSocket } from '../composables/usePointSourceDocuments';

const props = withDefaults(
  defineProps<{
    mapId: string | number | null;
    nodeId: number | null;
    /** Whether documents can be used: an LLM is connected. */
    canAttach?: boolean;
  }>(),
  { canAttach: true }
);

const { t } = useI18n();
const store = useStore();

const { documents, uploading, error, upload, attachUrl, remove } = usePointSourceDocuments({
  mapId: () => props.mapId,
  nodeId: () => props.nodeId,
  getSocket: () =>
    (store.getters['simultaneousEditing/getSocket'] as StatusSocket | null) ?? null,
});

const open = ref(false);
const trigger = ref<HTMLButtonElement | null>(null);

function openPopup(): void {
  if (props.canAttach) open.value = true;
}

function closePopup(): void {
  open.value = false;
  trigger.value?.focus();
}

watch(
  () => props.canAttach,
  () => {
    open.value = false;
  }
);
</script>

<style scoped lang="scss">
.point-sources__row {
  display: flex;
  align-items: center;
  gap: 15px;
}

.point-sources__trigger {
  flex: 0 0 auto;

  &.disabled {
    opacity: 0.5;
  }
}

.point-sources__count {
  margin-left: 6px;
}

.point-sources__hint {
  margin: 0;
  opacity: 0.8;
}
</style>
